import dayjs from 'dayjs';
import { Duration } from 'google-protobuf/google/protobuf/duration_pb';
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb';

/**
 * Helpers
 */
export const floatTo16BitPCM = (output: ArrayBuffer, input: Float32Array, offset?: number) => {
  const outView = new DataView(output);
  if (!offset) offset = 0;
  for (let i = 0; i < input.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, input[i]));
    outView.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
};

export const writeString = (view: DataView, offset: number, string: string) => {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
};

export const durationToFloat = (duration?: Duration): number => {
  if (!duration) return 0;
  return duration.getSeconds() + duration.getNanos() * 1e-9;
};

export const durationObjectToFloat = (duration?: Duration.AsObject): number => {
  if (!duration) return 0;
  return duration.seconds + duration.nanos * 1e-9;
};

export const floatToDuration = (seconds: number): Duration => {
  const dur = new Duration();
  dur.setSeconds(Math.floor(seconds));
  dur.setNanos(Math.floor((seconds - Math.floor(seconds)) * 1e9));
  return dur;
};

export const floatToDurationObject = (seconds: number): Duration.AsObject => {
  return {
    seconds: Math.floor(seconds),
    nanos: Math.floor((seconds - Math.floor(seconds)) * 1e9),
  };
};

export const timestampToString = (ts?: Timestamp.AsObject): string => {
  if (!ts) return '';
  const date = new Date(ts.seconds * 1e3 + ts.nanos * 1e-6);
  return dayjs(date).format('L LT');
};

export const durationObjectToDuration = (durObj?: Duration.AsObject) => {
  const duration = new Duration();
  duration.setSeconds(durObj ? durObj.seconds : 0);
  duration.setNanos(durObj ? durObj.nanos : 0);
  return duration;
};

export const durationToString = (dur?: Duration.AsObject): string => {
  if (!dur) return '';
  const durSeconds = durationObjectToFloat(dur);
  return floatDurationToString(durSeconds);
};

export const durationStringToDuration = (dur?: string): Duration => {
  if (!dur) return new Duration();
  return floatToDuration(parseFloat(dur));
};

export const floatDurationToString = (durSeconds: number): string => {
  const hours = Math.floor((durSeconds / 3600) % 60);
  const minutes = Math.floor((durSeconds / 60) % 60);
  const seconds = durSeconds % 60;

  return (
    (hours > 0 ? hours.toFixed(0).padStart(2, '0') + ':' : '') +
    (minutes.toFixed(0).padStart(2, '0') + ':') +
    seconds.toFixed(2).padStart(5, '0')
  );
};

export const onNextFrame = (callback: () => void) => {
  window.setTimeout(() => {
    window.requestAnimationFrame(callback);
  }, 1000);
};

export const strCmp = (a: string, b: string) => {
  if (a > b) {
    return 1;
  } else if (a < b) {
    return -1;
  } else {
    return 0;
  }
};

export const timestampCmp = (a?: Timestamp.AsObject, b?: Timestamp.AsObject) => {
  if (!a || !b) {
    return 0;
  }
  if (a.seconds === b.seconds) {
    return a.nanos - b.nanos;
  }
  return a.seconds - b.seconds;
};

export const languageCodeToFriendly = (languageCode: string) => {
  switch (languageCode) {
    case 'is-IS':
      return 'Íslenska (eldri)';
    case 'is-IS-x-althingi':
      return 'Íslenska (Alþingi)';
    case 'en-US':
      return 'Enska (Bandaríkin)';
    case 'is-IS-x-radiology':
      return 'Íslenska (röntgen - LSH)';
    case 'is-IS-x-rtg-rdhv':
      return 'Íslenska (röntgen - RD & HV)';
    case 'is-IS-x-exp':
      return 'Íslenska';
    case 'is-IS-x-folk':
      return 'Íslenska (ÍSMÚS)';
    default:
      return languageCode;
  }
};

export const isTransientGrpcError = (error: any): boolean => {
  const message = typeof error === 'string' ? error : error?.message;
  if (!message) return false;
  return (
    message.includes('Response closed without headers') ||
    message.includes('Http response at indicates failure or is not a valid status code: 504') ||
    message.includes('Http response at indicates failure or is not a valid status code: 502') ||
    message.includes('Http response at indicates failure or is not a valid status code: 503')
  );
};
